import React, { useEffect, useRef, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import fc25 from "../assets/fc25.png";
import gta from "../assets/gta.png";
import ps4 from "../assets/ps4.png";
import ps4occupe from "../assets/ps4occupe.png";
import ps5 from "../assets/ps5.png";
import ps5occupe from "../assets/ps5occupe.png";
import beepSound from "../assets/beep.mp3";
import axios from "axios";
import { SelectionContext } from "../context/SelectionContext";

const statusColors = {
  libre: "#00FF99",
  occupé: "#FFD700",
};

const games = [
  { id: "fc25", name: "FC 25", type: "match" },
  { id: "gta", name: "GTA V", type: "duration" },
];

const prices = {
  PS4: {
    "FC 25": "1.400 DT",
    "GTA V": { "1h": "4 DT", "30min": "2 DT", "15min": "1 DT" },
  },
  PS5: {
    "FC 25": "1.800 DT",
    "GTA V": { "1h": "5 DT", "30min": "2.5 DT", "15min": "1.5 DT" },
  },
};

const secondsMap = {
  "15min": 15 * 60,
  "30min": 30 * 60,
  "1h": 60 * 60,
};

const ConsoleCard = ({ consoleData, setConsoles, index }) => {
  const { id, plateforme, statut, jeu } = consoleData;
  const [matchCount, setMatchCount] = useState(0);
  const [durations, setDurations] = useState([]);
  const [timer, setTimer] = useState(0);
  const [beeped, setBeeped] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [error, setError] = useState("");
  const audioRef = useRef(null);

  const { selections, setSelections } = useContext(SelectionContext);

  useEffect(() => {
    const saved = selections[id];
    if (saved) {
      setConsoles((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                jeu: saved.jeu,
                statut: saved.jeu ? "occupé" : "libre",
              }
            : c
        )
      );
      if (saved.matchCount) setMatchCount(saved.matchCount);
      if (saved.durations) {
        setDurations(saved.durations);
        setTimer(saved.durations.reduce((acc, d) => acc + d.seconds, 0));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignGame = (game) => {
    const updatedConsole = { ...consoleData, jeu: game, statut: "occupé" };
    setConsoles((prev) =>
      prev.map((c) => (c.id === id ? updatedConsole : c))
    );
    setSelections((prev) => ({
      ...prev,
      [id]: { jeu: game, matchCount: 0, durations: [] },
    }));
  };

  const cancelGameSelection = () => {
    setConsoles((prev) =>
      prev.map((c) => (c.id === id ? { ...c, jeu: null, statut: "libre" } : c))
    );
    setMatchCount(0);
    setDurations([]);
    setTimer(0);
    setBeeped(false);
    setSelections((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 3000);
  };

  const endSession = async () => {
    if (!jeu || !plateforme) {
      showError("Veuillez sélectionner un jeu.");
      return;
    }

    const quantity =
      jeu.type === "match"
        ? matchCount
        : Math.round(durations.reduce((acc, d) => acc + d.seconds, 0) / 60);

    const totalPrice = parseFloat(getTotalPrice().replace(" DT", ""));
    if (totalPrice <= 0 || isNaN(totalPrice)) {
      showError("Ajoutez au moins un match ou une durée.");
      return;
    }

    const username = JSON.parse(localStorage.getItem("user"))?.username || "inconnu";

    const sessionData = {
      consoleName: consoleData.name,
      game: jeu.name,
      type: jeu.type,
      quantity,
      price: totalPrice,
      manager: username,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/sessions`, sessionData);
      setShowPrice(true);
      setTimeout(() => {
        setShowPrice(false);
        cancelGameSelection();
      }, 1000);
    } catch (error) {
      console.error("Erreur lors de l'envoi de la session :", error);
      showError("Erreur lors de l'enregistrement.");
    }
  };

  const addMatch = () => {
    const newCount = matchCount + 1;
    setMatchCount(newCount);
    updateSelection({ matchCount: newCount });
  };

  const removeMatch = () => {
    const newCount = Math.max(0, matchCount - 1);
    setMatchCount(newCount);
    updateSelection({ matchCount: newCount });
  };

  const addDuration = (label) => {
    const seconds = secondsMap[label];
    const newDurations = [...durations, { label, seconds }];
    setDurations(newDurations);
    setTimer((prev) => prev + seconds);
    setBeeped(false);
    updateSelection({ durations: newDurations });
  };

  const removeLastDuration = () => {
    if (durations.length === 0) return;
    const last = durations[durations.length - 1];
    const updated = durations.slice(0, -1);
    setDurations(updated);
    setTimer((t) => Math.max(0, t - last.seconds));
    updateSelection({ durations: updated });
  };

  const resetDuration = () => {
    setDurations([]);
    setTimer(0);
    setBeeped(false);
    updateSelection({ durations: [] });
  };

  const updateSelection = (updates) => {
    setSelections((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        ...updates,
      },
    }));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timer <= 60 && timer > 0 && !beeped) {
      audioRef.current?.play().catch(() => {});
      setBeeped(true);
    }
    if (timer === 0) setBeeped(false);
  }, [timer, beeped]);

  const getConsoleImage = () => {
    const busy = statut === "occupé";
    if (plateforme === "PS5") return busy ? ps5occupe : ps5;
    if (plateforme === "PS4") return busy ? ps4occupe : ps4;
    return ps4;
  };

  const getGameImage = (gameName) =>
    gameName?.toLowerCase().includes("fc") ? fc25 : gta;

  const color = statusColors[statut] || statusColors.libre;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const getTotalPrice = () => {
    if (!jeu || !plateforme) return "0 DT";
    if (jeu.type === "match") {
      const price = parseFloat(prices[plateforme][jeu.name]);
      return (price * matchCount).toFixed(3) + " DT";
    }
    if (jeu.type === "duration") {
      return (
        durations.reduce(
          (total, d) =>
            total +
            parseFloat(prices[plateforme][jeu.name][d.label].replace(" DT", "")),
          0
        ).toFixed(3) + " DT"
      );
    }
    return "0 DT";
  };

  return (
    <motion.div
      className="relative rounded-xl p-4 transition-all hover:scale-105 overflow-hidden"
      style={{
        border: `2px solid ${color}`,
        boxShadow: `0 0 20px ${color}`,
        background: "linear-gradient(145deg, #1e1e1e, #121212)",
      }}
    >
      <audio ref={audioRef} src={beepSound} preload="auto" />

      <AnimatePresence>
        {showPrice && (
          <motion.div
            className="absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <p className="text-5xl font-extrabold text-green-400 drop-shadow-lg animate-pulse">
              {getTotalPrice()}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded shadow z-50 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center justify-center mb-4">
        <motion.img
          src={getConsoleImage()}
          alt={plateforme}
          className="h-32 floating"
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>

      <h2 className="text-center text-lg font-bold text-white mb-2">
        {consoleData.name}
      </h2>

       {jeu ? (
              <div className="text-white text-center">
                <img
                  src={getGameImage(jeu.name)}
                  alt={jeu.name}
                  className="h-20 object-contain mb-2 mx-auto"
                />
                <p className="text-sm">Jeu : {jeu.name}</p>
                <p className="text-sm mt-1">Tarif actuel : {getTotalPrice()}</p>
      
                {jeu.type === "match" ? (
                  <>
                    <p className="mt-2">Matchs : {matchCount}</p>
                    <div className="flex justify-center gap-2 mt-2">
                      <button onClick={addMatch} className="bg-green-600 px-3 py-1 rounded text-white text-sm">+1 Match</button>
                      <button onClick={removeMatch} className="bg-red-600 px-3 py-1 rounded text-white text-sm">-1 Match</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-center gap-2 flex-wrap mt-2">
                      {Object.keys(prices[plateforme][jeu.name]).map((d) => (
                        <button key={d} onClick={() => addDuration(d)} className="bg-blue-600 px-3 py-1 rounded text-white text-sm">
                          {d}
                        </button>
                      ))}
                    </div>
                    {durations.length > 0 && (
                      <div className="mt-3">
                        <p>Temps ajouté : {durations.map((d) => d.label).join(" + ") || "aucun"}</p>
                        <div className="flex justify-center gap-2 mt-2">
                          <button onClick={removeLastDuration} className="bg-yellow-600 px-3 py-1 rounded text-white text-sm">- dernier</button>
                          <button onClick={resetDuration} className="bg-red-600 px-3 py-1 rounded text-white text-sm">Réinitialiser</button>
                        </div>
                      </div>
                    )}
                    {timer > 0 && (
                      <p className={`mt-3 text-md font-bold ${timer <= 60 ? "text-red-400 animate-pulse" : "text-green-400"}`}>
                        Temps restant : {formatTime(timer)}
                      </p>
                    )}
                  </>
                )}
      
                <div className="flex justify-center gap-2 mt-4 flex-wrap">
                  <button onClick={cancelGameSelection} className="bg-gray-500 px-3 py-1 rounded text-white text-sm">Retour</button>
                  <button onClick={endSession} className="bg-yellow-400 px-3 py-1 rounded text-black text-sm">Fin session</button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-center text-gray-400 mb-2">Aucun jeu sélectionné</p>
                <div className="flex justify-center gap-4 flex-wrap mt-3">
                  {games.map((g) => (
                    <div key={g.id} onClick={() => assignGame(g)} className="cursor-pointer border-2 border-gray-500 rounded-xl p-4 w-36 sm:w-44 hover:border-green-500 hover:scale-105 transition-all bg-[#1e1e1e]">
                      <img src={getGameImage(g.name)} alt={g.name} className="h-24 mx-auto mb-3 object-contain" />
                      <p className="text-center text-sm font-semibold text-white">{g.name}</p>
                      <p className="text-center text-xs text-gray-400 italic">{g.type}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
      
            <p className="text-center mt-4 mb-2">
              Statut : <span style={{ color }}>{statut.toUpperCase()}</span>
            </p>
          </motion.div>
        );
      };
      
      export default ConsoleCard;
      